import { useEffect, useState } from 'react';
import useSalesBotStore from '@/stores/SalesBotStore';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { saveAutopilotSettings } from '@/hooks/sales-bot';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';

const AutopilotFollowup = () => {
  const [schedulingPromptValue, setSchedulingPromptValue] = useState('');
  const [messagePromptValue, setMessagePromptValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [localFollowupEnabled, setLocalFollowupEnabled] = useState(false);

  const {
    followupSchedulingPrompt,
    followupMessagePrompt,
    followupEnabled,
    updateFollowupSchedulingPrompt,
    updateFollowupMessagePrompt,
    setFollowupEnabled,
  } = useSalesBotStore(
    useShallow((state) => ({
      followupSchedulingPrompt: state.followupSchedulingPrompt,
      followupMessagePrompt: state.followupMessagePrompt,
      followupEnabled: state.followupEnabled,
      updateFollowupSchedulingPrompt: state.updateFollowupSchedulingPrompt,
      updateFollowupMessagePrompt: state.updateFollowupMessagePrompt,
      setFollowupEnabled: state.setFollowupEnabled,
    }))
  );

  // Initialize followup enabled from store
  useEffect(() => {
    setLocalFollowupEnabled(followupEnabled);
  }, [followupEnabled]);

  // Initialize prompt values from store when they're available
  useEffect(() => {
    if (followupSchedulingPrompt) {
      setSchedulingPromptValue(followupSchedulingPrompt);
    }
  }, [followupSchedulingPrompt]);

  useEffect(() => {
    if (followupMessagePrompt) {
      setMessagePromptValue(followupMessagePrompt);
    }
  }, [followupMessagePrompt]);

  const handleToggleFollowup = async (checked) => {
    try {
      setLocalFollowupEnabled(checked);

      // Build the payload conditionally
      const payload = {
        followup_enabled: checked,
      };

      // Only include prompt values if followup is enabled and prompts exist
      if (checked) {
        if (schedulingPromptValue.trim() !== '') {
          payload.autopilot_followup_scheduling_prompt = schedulingPromptValue;
        }
        if (messagePromptValue.trim() !== '') {
          payload.autopilot_followup_message_prompt = messagePromptValue;
        }
      }

      await toast.promise(saveAutopilotSettings(payload), {
        loading: checked ? 'Enabling followup...' : 'Disabling followup...',
        success: () => {
          setFollowupEnabled(checked);
          if (checked && schedulingPromptValue && messagePromptValue) {
            updateFollowupSchedulingPrompt(schedulingPromptValue);
            updateFollowupMessagePrompt(messagePromptValue);
          }
          return checked
            ? 'Followup enabled successfully'
            : 'Followup disabled successfully';
        },
        error: (error) => {
          // Revert the checkbox state on error
          setLocalFollowupEnabled(!checked);
          return (
            error.response?.data?.message ||
            'Failed to update followup settings'
          );
        },
      });
    } catch (error) {
      // Revert the checkbox state on error
      setLocalFollowupEnabled(!checked);
      toast.error('Failed to update followup settings');
    }
  };

  const handleSavePrompts = async () => {
    try {
      if (localFollowupEnabled) {
        if (schedulingPromptValue.trim() === '') {
          toast.error('Please enter a followup scheduling prompt');
          return;
        }
        if (messagePromptValue.trim() === '') {
          toast.error('Please enter a followup message prompt');
          return;
        }
      }

      setIsSaving(true);

      // Build the payload conditionally
      const payload = {
        followup_enabled: localFollowupEnabled,
      };

      // Only include prompt values if followup is enabled
      if (localFollowupEnabled) {
        payload.autopilot_followup_scheduling_prompt = schedulingPromptValue;
        payload.autopilot_followup_message_prompt = messagePromptValue;
      }

      await toast.promise(saveAutopilotSettings(payload), {
        loading: 'Saving settings...',
        success: () => {
          setFollowupEnabled(localFollowupEnabled);
          if (localFollowupEnabled) {
            updateFollowupSchedulingPrompt(schedulingPromptValue);
            updateFollowupMessagePrompt(messagePromptValue);
          }
          setIsSaving(false);
          return localFollowupEnabled
            ? 'Followup settings saved successfully'
            : 'Followup disabled successfully';
        },
        error: (error) => {
          setIsSaving(false);
          return error.response?.data?.message || 'Failed to save settings';
        },
      });
    } catch (error) {
      toast.error('Failed to save settings');
      setIsSaving(false);
    }
  };

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      {/* Followup Toggle Section */}
      <div className='flex-shrink-0 border-b p-6'>
        <div className='flex items-center justify-between'>
          <div className='flex-1'>
            <p className='text-sm text-muted-foreground'>
              Enable follow-up messages to be sent to prospects based on the
              configured prompts below.
            </p>
          </div>
          <Checkbox
            id='enable-followup'
            checked={localFollowupEnabled}
            onCheckedChange={handleToggleFollowup}
            className='h-6 w-6'
          />
        </div>
      </div>

      <Separator />

      {/* Prompts Section */}
      <div className='flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row'>
        {/* Left Half - Followup Scheduling Prompt */}
        <div className='flex w-full flex-col border-b border-r-0 p-2 pb-4 lg:w-1/2 lg:border-b-0 lg:border-r lg:p-4'>
          <div className='mb-2'>
            <h2 className='text-lg font-semibold'>
              Followup Scheduling Prompt
            </h2>
            <p className='text-sm text-muted-foreground'>
              Configure when and how the autopilot should schedule follow-up
              messages.
            </p>
          </div>
          <div className='flex flex-1 flex-col'>
            <Textarea
              value={schedulingPromptValue}
              onChange={(e) => setSchedulingPromptValue(e.target.value)}
              placeholder='Enter your followup scheduling prompt here...'
              disabled={!localFollowupEnabled}
              className='custom-scrollbar mb-2 h-[58vh] max-h-[100vh] resize-none'
            />
          </div>
        </div>

        {/* Right Half - Followup Message Prompt */}
        <div className='flex w-full flex-col p-2 pb-4 lg:w-1/2 lg:p-4'>
          <div className='mb-2'>
            <h2 className='text-lg font-semibold'>Followup Message Prompt</h2>
            <p className='text-sm text-muted-foreground'>
              Configure the actual follow-up messages sent at each attempt
              level.
            </p>
          </div>
          <div className='flex flex-1 flex-col'>
            <Textarea
              value={messagePromptValue}
              onChange={(e) => setMessagePromptValue(e.target.value)}
              placeholder='Enter your followup message prompt here...'
              disabled={!localFollowupEnabled}
              className='custom-scrollbar mb-4 h-[58vh] max-h-[100vh] resize-none'
            />
            <Button
              onClick={handleSavePrompts}
              disabled={isSaving || !localFollowupEnabled}
              className='w-64 self-end'
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutopilotFollowup;
